import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { supabase } from '../../../supabase/supabase.client';
import { Property, PropertyImage, PropertyService } from '../../../services/property.service';

@Component({
  selector: 'app-manage-properties',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './manage-properties.component.html',
  styleUrl: './manage-properties.component.css'
})
export class ManagePropertiesComponent implements OnInit {
  properties = signal<Property[]>([]);
  loading = signal(true);
  error = signal('');
  searchQuery = signal('');
  deleting = signal<string | null>(null);
  selectedIds = signal<Set<string>>(new Set());
  bulkActionInProgress = signal(false);
  viewMode = signal<'grid' | 'list'>('list');
  
  // Filters
  statusFilter = signal<'all' | 'published' | 'draft'>('all');
  propertyTypeFilter = signal<string>('all');
  priceRangeFilter = signal<string>('all');
  locationFilter = signal<string>('all');
  
  // Pagination
  currentPage = signal(1);
  itemsPerPage = 10;
  Math = Math;

  // Statistics computed properties
  get totalPropertiesCount(): number {
    return this.properties().length;
  }

  get publishedCount(): number {
    return this.properties().filter(p => p.is_published).length;
  }

  get draftCount(): number {
    return this.properties().filter(p => !p.is_published).length;
  }

  get totalValue(): number {
    return this.properties().reduce((sum, p) => sum + (p.price || 0), 0);
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public propertyService: PropertyService
  ) {}

  ngOnInit() {
    this.loadProperties();
    
    // Reload properties when returning from edit (query param changes)
    this.route.queryParams.subscribe(params => {
      if (params['refresh']) {
        this.loadProperties();
      }
    });
  }

  async loadProperties() {
    try {
      this.loading.set(true);
      this.error.set('');

      const { data, error } = await supabase
        .from('properties')
        .select(`
          *,
          property_images (
            id,
            property_id,
            storage_path,
            alt_text,
            is_primary,
            position
          )
        `)
        .order('created_at', { ascending: false });

      if (error) {
        this.error.set(`Failed to load properties: ${error.message}`);
        console.error('Error loading properties:', error);
      } else {
        this.properties.set(data as Property[]);
        // Pre-load all image URLs
        await this.propertyService.preloadImageUrls(data as Property[]);
      }
    } catch (err: any) {
      this.error.set(`Error: ${err.message}`);
      console.error('Unexpected error:', err);
    } finally {
      this.loading.set(false);
    }
  }



  get filteredProperties(): Property[] {
    let filtered = this.properties();
    
    // Apply search query
    const query = this.searchQuery().toLowerCase();
    if (query) {
      filtered = this.propertyService.filterProperties(filtered, this.searchQuery());
    }
    
    // Apply status filter
    if (this.statusFilter() !== 'all') {
      filtered = filtered.filter(p => 
        this.statusFilter() === 'published' ? p.is_published : !p.is_published
      );
    }
    
    // Apply property type filter
    if (this.propertyTypeFilter() !== 'all') {
      filtered = filtered.filter(p => p.property_type === this.propertyTypeFilter());
    }
    
    // Apply price range filter
    if (this.priceRangeFilter() !== 'all') {
      const [min, max] = this.priceRangeFilter().split('-').map(Number);
      filtered = filtered.filter(p => {
        const price = p.price;
        if (max) {
          return price >= min && price <= max;
        } else {
          return price >= min;
        }
      });
    }
    
    // Apply location filter
    if (this.locationFilter() !== 'all') {
      filtered = filtered.filter(p => 
        p.city?.toLowerCase().includes(this.locationFilter().toLowerCase()) ||
        p.state?.toLowerCase().includes(this.locationFilter().toLowerCase())
      );
    }
    
    return filtered;
  }
  
  get paginatedProperties(): Property[] {
    const start = (this.currentPage() - 1) * this.itemsPerPage;
    const end = start + this.itemsPerPage;
    return this.filteredProperties.slice(start, end);
  }
  
  get totalPages(): number {
    return Math.ceil(this.filteredProperties.length / this.itemsPerPage);
  }
  
  get pageNumbers(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage();
    const pages: (number | string)[] = [];
    
    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      } else if (current >= total - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = total - 3; i <= total; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(total);
      }
    }
    
    return pages;
  }
  
  get uniquePropertyTypes(): string[] {
    const types = new Set(this.properties().map(p => p.property_type));
    return Array.from(types).sort();
  }
  
  get uniqueLocations(): string[] {
    const locations = new Set<string>();
    this.properties().forEach(p => {
      if (p.city) locations.add(p.city);
      if (p.state && p.state !== p.city) locations.add(p.state);
    });
    return Array.from(locations).sort();
  }
  
  goToPage(page: number | string) {
    if (typeof page === 'number' && page >= 1 && page <= this.totalPages) {
      this.currentPage.set(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  nextPage() {
    if (this.currentPage() < this.totalPages) {
      this.currentPage.update(page => page + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  previousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update(page => page - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }
  
  resetFilters() {
    this.statusFilter.set('all');
    this.propertyTypeFilter.set('all');
    this.priceRangeFilter.set('all');
    this.locationFilter.set('all');
    this.searchQuery.set('');
    this.resetFilters();
  }

  editProperty(propertyId: string) {
    this.router.navigate(['/admin/edit-property', propertyId]);
  }

  async deleteProperty(propertyId: string) {
    if (!confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return;
    }

    this.deleting.set(propertyId);
    this.error.set('');
    
    try {
      console.log('Deleting property:', propertyId);
      
      // Delete associated images from storage and database
      const property = this.properties().find(p => p.id === propertyId);
      if (property?.property_images && property.property_images.length > 0) {
        console.log(`Deleting ${property.property_images.length} images for property ${propertyId}`);
        
        for (const image of property.property_images) {
          // Delete from storage
          const { error: storageError } = await supabase.storage
            .from('property_images')
            .remove([image.storage_path]);

          if (storageError) {
            console.warn('Error deleting image from storage:', storageError);
            console.warn('Storage path attempted:', image.storage_path);
          } else {
            console.log('Successfully deleted image:', image.storage_path);
          }
        }
      }

      // Delete property images from database
      console.log('Deleting property images from database...');
      const { error: deleteImagesError } = await supabase
        .from('property_images')
        .delete()
        .eq('property_id', propertyId);

      if (deleteImagesError) {
        console.error('Error deleting images from database:', deleteImagesError);
        throw new Error(`Failed to delete property images: ${deleteImagesError.message}`);
      }

      // Delete property from database
      console.log('Deleting property from database...');
      const { error: deleteError } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);

      if (deleteError) {
        console.error('Error deleting property:', deleteError);
        this.error.set(`Failed to delete property: ${deleteError.message}`);
        throw deleteError;
      }

      console.log('Successfully deleted property');
      
      // Remove from local array
      this.properties.update(props => 
        props.filter(p => p.id !== propertyId)
      );
      
      alert('Property deleted successfully');
      
    } catch (err: any) {
      console.error('Delete property error:', err);
      this.error.set(`Error: ${err.message}`);
      alert(`Error: ${err.message}`);
    } finally {
      this.deleting.set(null);
    }
  }

  togglePublish(property: Property) {
    this.updateProperty(property.id, {
      is_published: !property.is_published
    });
  }

  private async updateProperty(propertyId: string, updates: any) {
    try {
      const { error } = await supabase
        .from('properties')
        .update(updates)
        .eq('id', propertyId);

      if (error) {
        this.error.set(`Failed to update property: ${error.message}`);
        console.error('Error updating property:', error);
      } else {
        // Update local state
        this.properties.update(props =>
          props.map(p => 
            p.id === propertyId ? { ...p, ...updates } : p
          )
        );
      }
    } catch (err: any) {
      this.error.set(`Error: ${err.message}`);
      console.error('Unexpected error:', err);
    }
  }

  addNewListing() {
    this.router.navigate(['/admin/new-listing']);
  }

  goBack() {
    this.router.navigate(['/admin']);
  }

  // Bulk selection methods
  toggleSelection(propertyId: string) {
    this.selectedIds.update(ids => {
      const newIds = new Set(ids);
      if (newIds.has(propertyId)) {
        newIds.delete(propertyId);
      } else {
        newIds.add(propertyId);
      }
      return newIds;
    });
  }

  toggleSelectAll() {
    if (this.isAllSelected()) {
      this.selectedIds.set(new Set());
    } else {
      const allIds = new Set(this.filteredProperties.map(p => p.id));
      this.selectedIds.set(allIds);
    }
  }

  isSelected(propertyId: string): boolean {
    return this.selectedIds().has(propertyId);
  }

  isAllSelected(): boolean {
    const filtered = this.filteredProperties;
    return filtered.length > 0 && filtered.every(p => this.selectedIds().has(p.id));
  }

  get selectedCount(): number {
    return this.selectedIds().size;
  }

  clearSelection() {
    this.selectedIds.set(new Set());
  }

  // Bulk actions
  async bulkPublish() {
    if (this.selectedCount === 0) return;
    
    if (!confirm(`Publish ${this.selectedCount} selected properties?`)) {
      return;
    }

    this.bulkActionInProgress.set(true);
    try {
      const ids = Array.from(this.selectedIds());
      const { error } = await supabase
        .from('properties')
        .update({ is_published: true })
        .in('id', ids);

      if (error) {
        this.error.set(`Failed to publish properties: ${error.message}`);
      } else {
        this.properties.update(props =>
          props.map(p => 
            ids.includes(p.id) ? { ...p, is_published: true } : p
          )
        );
        this.clearSelection();
      }
    } catch (err: any) {
      this.error.set(`Error: ${err.message}`);
    } finally {
      this.bulkActionInProgress.set(false);
    }
  }

  async bulkUnpublish() {
    if (this.selectedCount === 0) return;
    
    if (!confirm(`Unpublish ${this.selectedCount} selected properties?`)) {
      return;
    }

    this.bulkActionInProgress.set(true);
    try {
      const ids = Array.from(this.selectedIds());
      const { error } = await supabase
        .from('properties')
        .update({ is_published: false })
        .in('id', ids);

      if (error) {
        this.error.set(`Failed to unpublish properties: ${error.message}`);
      } else {
        // Reload properties to ensure UI is in sync
        await this.loadProperties();
        this.clearSelection();
      }
    } catch (err: any) {
      this.error.set(`Error: ${err.message}`);
    } finally {
      this.bulkActionInProgress.set(false);
    }
  }

  async bulkDelete() {
    if (this.selectedCount === 0) return;
    
    if (!confirm(`Delete ${this.selectedCount} selected properties? This action cannot be undone.`)) {
      return;
    }

    this.bulkActionInProgress.set(true);
    this.error.set('');
    
    try {
      const ids = Array.from(this.selectedIds());
      console.log('Starting bulk delete for property IDs:', ids);
      
      // Delete images for each property
      for (const id of ids) {
        const property = this.properties().find(p => p.id === id);
        if (property?.property_images && property.property_images.length > 0) {
          console.log(`Deleting ${property.property_images.length} images for property ${id}`);
          for (const image of property.property_images) {
            const { error: storageError } = await supabase.storage
              .from('property_images')
              .remove([image.storage_path]);
            
            if (storageError) {
              console.warn(`Failed to delete image ${image.storage_path}:`, storageError);
            }
          }
        }
      }

      // Delete property images from database
      console.log('Deleting property images from database...');
      const { error: imagesError } = await supabase
        .from('property_images')
        .delete()
        .in('property_id', ids);

      if (imagesError) {
        console.error('Error deleting property images:', imagesError);
        throw new Error(`Failed to delete property images: ${imagesError.message}`);
      }

      // Delete properties
      console.log('Deleting properties from database...');
      const { error: deleteError, count } = await supabase
        .from('properties')
        .delete()
        .in('id', ids);

      if (deleteError) {
        console.error('Error deleting properties:', deleteError);
        this.error.set(`Failed to delete properties: ${deleteError.message}`);
        throw deleteError;
      }

      console.log(`Successfully deleted ${ids.length} properties`);
      
      // Update local state
      this.properties.update(props =>
        props.filter(p => !ids.includes(p.id))
      );
      
      this.clearSelection();
      
      // Show success message
      alert(`Successfully deleted ${ids.length} propert${ids.length === 1 ? 'y' : 'ies'}`);
      
    } catch (err: any) {
      console.error('Bulk delete error:', err);
      this.error.set(`Error during deletion: ${err.message}`);
      alert(`Error: ${err.message}`);
    } finally {
      this.bulkActionInProgress.set(false);
    }
  }

  toggleViewMode() {
    this.viewMode.update(mode => mode === 'grid' ? 'list' : 'grid');
  }
}
